import config from '../config/scale';

export const Scale = function (qztray) {
    /**
     *  @type {QzTray}
     */
    this.qztray = qztray;

    /**
     *  @type {(?number)}
     */
    this.weight = null;

    /**
     *  @type {boolean}
     */
    this.settled = false;

    /**
     *  @type {?function}
     */
    this.callback = null;

    /**
     * @type {boolean}
     */
    this.loading = false;

    /**
     * @type {boolean}
     */
    this.connected = false;

    /**
     * @type {boolean}
     */
    this.connecting = false;

    /**
     * @type {boolean}
     */
    this.failed = false;

    /**
     * @type {boolean}
     */
    this.disconnecting = false;

    /**
     * Parse the response from the scale
     *
     * @param rawWeight
     * @returns {number}
     */
    this.parseWeight = function (rawWeight) {
        let decimalWeight = parseFloat(rawWeight.replace(/[^0-9.-]/g, ''));

        if (rawWeight.indexOf('ST') > -1) {
            this.settled = true;
        } else if(rawWeight.indexOf('US') > -1) {
            this.settled = false;
        } else {
            // two consecutive equivalent readings
            this.settled = (decimalWeight === this.weight);
        }

        return decimalWeight;
    };

    /**
     * @param callback
     * @returns {Promise<never>|Promise<void>|Promise<T>}
     */
    this.connect = function (callback) {
        let scale = this;
        scale.connecting = true;
        //console.log('scale connecting', this.connecting);

        scale.callback = callback;

        if (! scale.qztray.connected) {
            return Promise.reject('No connection with QZ is active.');
        }

        if (scale.connected) {
            return Promise.resolve()
                .then(this._connectResolve)
                .finally(this._connectFinally);
        }

        let settings = JSON.parse(localStorage.getItem('settings') || '{}');
        let port = settings.scalePort || config.port;
        let portOptions = localStorage.getItem('qzScalePortOptions') || config.portOptions;

        let _readLoop = function (streamEvent) {
            let raw = streamEvent.output;
            let rawWeight = raw.split(/[\r\n]+/).slice(-2, -1)[0] || '';

            scale.weight = scale.parseWeight(rawWeight);

            if (scale.callback) {
                scale.callback(scale.weight, scale.settled);
            }
        };

        return scale.qztray.qz.serial.openPort(port, portOptions)
            .then(() => {
                scale.device = scale.qztray.qz.serial;
                scale.qztray.qz.serial.setSerialCallbacks(_readLoop);
            })
            .then(this._connectResolve)
            .catch(this._portError)
            .finally(this._connectFinally);
    };

    /**
     * @returns {(Promise<unknown> | Promise<T>)|Promise<void>}
     */
    this.disconnect = function () {
        let scale = this;
        scale.disconnecting = true;
        //console.log('scale disconnecting', this.disconnecting);

        if (! scale.qztray.connected) {
            return Promise.reject('No connection with QZ active.');
        }

        if (! scale.connected) {
            return Promise.resolve()
                .then(this._disconnectResolve)
                .finally(this._disconnectFinally);
        }

        let settings = JSON.parse(localStorage.getItem('settings') || '{}');
        let port = settings.scalePort || config.port;

        scale.device = null;
        return scale.qztray.qz.serial.closePort(port)
            .then(this._disconnectResolve)
            .catch(this._portError)
            .finally(this._disconnectFinally);
    };

    /**
     * Returns the last weight received
     *
     * @returns {?number}
     */
    this.getWeight = function () {
        return this.weight;
    };

    /**
     * @private
     */
    this._connectResolve = () => {
        this.connected = true;
        this.failed = false;
        //console.log('scale connected', this.connected);
    };

    /**
     * @private
     */
    this._connectFinally = () => {
        this.connecting = false;
        //console.log('scale connecting', this.connecting);
    };

    /**
     * @private
     */
    this._disconnectResolve = () => {
        this.connected = false;
        this.failed = false;
        //console.log('scale connected', this.connected);
    };

    /**
     * @private
     */
    this._disconnectFinally = () => {
        this.disconnecting = false;
        //console.log('scale disconnecting', this.disconnecting);
    };

    /**
     * @param error
     * @private
     */
    this._portError = (error) => {
        this.failed = true;
        this.connected = false;
        this.connecting = false;
        this.disconnecting = false;

        console.error(error);
        errorToast(error);
    };
};

function errorToast(message) {
    const h = vm.$createElement;
    const vNodesMsg = h(
        'div',
        {class: 'd-flex align-items-center'},
        [
            h('div',
                {class: 'mr-2'},
                [
                    h('span', {class: 'fa-stack fa-lg fa-fw fa-pull-left'}, [
                        h('i', {class: 'fa-duotone fa-weight fa-fw fa-stack-1x'}, ''),
                        h('i', {class: 'fa-duotone fa-ban fa-fw fa-stack-2x text-danger'}, ''),
                    ]),
                ]
            ),
            h('div',
                {class: 'flex-fill'},
                [
                    h('strong', 'Scale Error.'),
                    h('button', {
                        class: 'close ml-auto mb-1',
                        on: {click: () => vm.$bvToast.hide('scale-error-toast')}
                    }, '×'),
                    h('div', {class: 'font-xs'}, `${message}`),
                    h('a', {class: 'font-xs', attrs: {href: '/profile/settings', target: '_blank'}}, [
                        'Open Settings',
                        h('i', {class: 'fa-duotone fa-external-link fa-fw'}, ''),
                    ])
                ]
            ),
        ],
    );
    vm.$bvToast.toast([vNodesMsg], {
        variant: 'secondary',
        toaster: 'b-toaster-bottom-right',
        id: 'scale-error-toast',
        autoHideDelay: 10000,
        noAutoHide: true,
        noCloseButton: true,
    });
}

export default Scale;
